<template>
  <div class="material-calendar-range">
    <div class="range-header">
      <button class="nav-btn" @click="prevMonth">
        <svg width="24" height="24" viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/></svg>
      </button>
      <span class="month-label">{{ currentMonthName }} {{ currentYear }}</span>
      <button class="nav-btn" @click="nextMonth">
        <svg width="24" height="24" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/></svg>
      </button>
    </div>

    <div class="calendar-grid">
      <div v-for="day in weekDays" :key="day" class="weekday-label">{{ day }}</div>
      
      <div 
        v-for="(date, index) in calendarDays" 
        :key="index"
        class="day-cell"
        :class="{
          'current-month': date.currentMonth,
          'selected-start': isStartDate(date.date),
          'selected-end': isEndDate(date.date),
          'in-range': isInRange(date.date),
          'has-data': hasData(date.date),
          'today': isToday(date.date)
        }"
        @click="selectDate(date.date)"
      >
        <span class="day-number">{{ date.day }}</span>
        <div class="data-dot" v-if="hasData(date.date)"></div>
      </div>
    </div>

    <div class="range-footer">
      <div class="selected-range" v-if="selectedStartDate && selectedEndDate">
        Selezionato: {{ formatDateDisplay(selectedStartDate) }} - {{ formatDateDisplay(selectedEndDate) }}
      </div>
      <div v-else class="range-instruction">
        Clicca per selezionare data di inizio e fine
      </div>
      <div class="range-actions">
        <button 
          class="btn-apply" 
          @click="applyRange"
          :disabled="!selectedStartDate || !selectedEndDate"
        >
          Applica
        </button>
        <button 
          class="btn-clear" 
          @click="clearRange"
        >
          Cancella
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch} from 'vue';

const props = defineProps<{
  activityDates?: string[]; // Array of YYYY-MM-DD with data
  selectedStartDate?: string; // YYYY-MM-DD
  selectedEndDate?: string; // YYYY-MM-DD
}>();

const emit = defineEmits(['close', 'range-select']);

function parseYYYYMMDD(dateStr?: string): Date | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const today = new Date();

const initialDate =
  parseYYYYMMDD(props.selectedEndDate) ||
  parseYYYYMMDD(props.selectedStartDate) ||
  today;

const currentMonth = ref(initialDate.getMonth());
const currentYear = ref(initialDate.getFullYear());

const weekDays = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];

const currentMonthName = computed(() => {
  return new Date(currentYear.value, currentMonth.value).toLocaleString('it-IT', { month: 'long' });
});

interface CalendarDay {
  date: Date;
  day: number;
  currentMonth: boolean;
}

const calendarDays = computed<CalendarDay[]>(() => {
  const days: CalendarDay[] = [];
  const firstDay = new Date(currentYear.value, currentMonth.value, 1);
  const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0);
  
  // Adjust logic for Monday start (0=Sun -> 6, 1=Mon -> 0)
  let startDayOfWeek = firstDay.getDay() - 1; 
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  // Previous month padding
  const prevMonthLastDay = new Date(currentYear.value, currentMonth.value, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
     days.push({
       date: new Date(currentYear.value, currentMonth.value - 1, prevMonthLastDay - i),
       day: prevMonthLastDay - i,
       currentMonth: false
     });
  }

  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({
      date: new Date(currentYear.value, currentMonth.value, i),
      day: i,
      currentMonth: true
    });
  }

  // Next month padding
  const remainingCells = 42 - days.length; // 6 rows * 7
  for (let i = 1; i <= remainingCells; i++) {
     days.push({
       date: new Date(currentYear.value, currentMonth.value + 1, i),
       day: i,
       currentMonth: false
     });
  }

  return days;
});

// Range selection logic
const selectedStartDate = ref<string>(props.selectedStartDate || '');
const selectedEndDate = ref<string>(props.selectedEndDate || '');

function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value--;
  } else {
    currentMonth.value--;
  }
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value++;
  } else {
    currentMonth.value++;
  }
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function isToday(date: Date) {
  return formatDate(date) === formatDate(new Date());
}

function hasData(date: Date) {
  if (!props.activityDates) return false;
  return props.activityDates.includes(formatDate(date));
}

function isStartDate(date: Date) {
  return selectedStartDate.value === formatDate(date);
}

function isEndDate(date: Date) {
  return selectedEndDate.value === formatDate(date);
}

function isInRange(date: Date) {
  if (!selectedStartDate.value || !selectedEndDate.value) return false;
  
  const dateStr = formatDate(date);
  const start = selectedStartDate.value;
  const end = selectedEndDate.value;
  
  // Ordina start e end (potrebbero essere selezionati in ordine inverso)
  const [sortedStart, sortedEnd] = [start, end].sort();
  
  return dateStr >= sortedStart && dateStr <= sortedEnd;
}

// function selectDate(date: Date) {
//   const dateStr = formatDate(date);
  
//   if (!selectedStartDate.value) {
//     // Prima selezione: imposta start date
//     selectedStartDate.value = dateStr;
//     selectedEndDate.value = dateStr;
//   } else if (!selectedEndDate.value) {
//     // Seconda selezione: imposta end date
//     selectedEndDate.value = dateStr;
    
//     // Se end date < start date, scambia
//     if (selectedEndDate.value < selectedStartDate.value) {
//       [selectedStartDate.value, selectedEndDate.value] = [selectedEndDate.value, selectedStartDate.value];
//     }
//   } else {
//     // Reset e ricomincia
//     selectedStartDate.value = dateStr;
//     selectedEndDate.value = '';
//   }
// }
function selectDate(date: Date) {
  const dateStr = formatDate(date);

  if (!selectedStartDate.value) {
    selectedStartDate.value = dateStr;
    selectedEndDate.value = '';
    return;
  }

  if (!selectedEndDate.value) {
    if (dateStr < selectedStartDate.value) {
      selectedEndDate.value = selectedStartDate.value;
      selectedStartDate.value = dateStr;
    } else {
      selectedEndDate.value = dateStr;
    }
    return;
  }

  selectedStartDate.value = dateStr;
  selectedEndDate.value = '';
}

function applyRange() {
  if (!selectedStartDate.value || !selectedEndDate.value) return;
  
  emit('range-select', {
    start: selectedStartDate.value,
    end: selectedEndDate.value
  });
  emit('close');
}

function clearRange() {
  selectedStartDate.value = '';
  selectedEndDate.value = '';
}

watch(
  () => props.selectedStartDate,
  (val) => {
    selectedStartDate.value = val || '';
  },
  { immediate: true }
);

watch(
  () => props.selectedEndDate,
  (val) => {
    selectedEndDate.value = val || '';
  },
  { immediate: true }
);

watch(
  () => [props.selectedStartDate, props.selectedEndDate],
  ([start, end]) => {
    const d = parseYYYYMMDD(end || start || '');
    if (d) {
      currentMonth.value = d.getMonth();
      currentYear.value = d.getFullYear();
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.material-calendar-range {
  background: #1E1E1E;
  border-radius: 16px;
  padding: 16px;
  width: 320px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
  font-family: 'Inter', sans-serif;
  border: 1px solid #333;
  user-select: none;
}

.range-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  color: #fff;
}

.month-label {
  font-weight: 600;
  text-transform: capitalize;
  font-size: 16px;
}

.nav-btn {
  background: transparent;
  border: none;
  color: #aaa;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-btn:hover {
  background: rgba(255,255,255,0.1);
  color: #fff;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  text-align: center;
}

.weekday-label {
  font-size: 12px;
  color: #888;
  font-weight: 500;
  margin-bottom: 4px;
}

.day-cell {
  height: 36px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  background: transparent;
}

.day-cell.current-month {
  color: #fff;
}

.day-cell:hover {
  background: rgba(255,255,255,0.1);
}

.day-cell.selected-start {
  background: #4CAF50 !important;
  color: #fff !important;
  font-weight: bold;
}

.day-cell.selected-end {
  background: #F44336 !important;
  color: #fff !important;
  font-weight: bold;
}

.day-cell.in-range {
  background: rgba(33, 150, 243, 0.3) !important;
  color: #fff !important;
  border-radius: 0;
}

.day-cell.in-range:first-child {
  border-radius: 50% 0 0 50%;
}

.day-cell.in-range:last-child {
  border-radius: 0 50% 50% 0;
}

.day-cell.today {
  border: 1px solid #2196F3;
}

.day-cell.has-data:not(.selected-start):not(.selected-end):not(.in-range) {
  border: 2px solid #2196F3;
  background: rgba(33, 150, 243, 0.15);
  color: #fff;
}

.data-dot {
  display: none;
}

.range-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #333;
  text-align: center;
}

.selected-range {
  color: #fff;
  font-size: 14px;
  margin-bottom: 12px;
}

.range-instruction {
  color: #888;
  font-size: 13px;
  margin-bottom: 12px;
}

.range-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.btn-apply {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-clear {
  background: #666;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
</style>